import React from 'react';
import { useFormikContext, FormikProps } from 'formik';

import { ROUTES } from 'src/constants';
import { validateFormikForm } from 'src/helpers';
import { IAuthStore, IUserStore, IResourcesStore, IJob } from '@src/models';
import { Flex, Delimiter, Link } from 'src/kit';
import { FormikInput, FormikCombobox, FormikSubmitButton, Title, Tooltip, SignInModal } from '@src/features/common';

import { isIE11 } from '@src/constants';
import { ValuesType } from './validationSchema';

type GeneralFormProps = FormikProps<ValuesType> & {
  authStore: IAuthStore;
  userStore: IUserStore;
  resourcesStore: IResourcesStore;
  job: IJob;
};

const GeneralForm = ({ authStore, userStore, resourcesStore, job, status, initialValues }: GeneralFormProps) => {
  const {
    values,
    errors,
    isValidating,
    validateForm,
    submitForm,
    setStatus,
    setErrors,
    setTouched
  } = useFormikContext();
  const [showSignInModal, setShowSignInModal] = React.useState(false);
  const [isEmailTaken, setIsEmailTaken] = React.useState(false);

  React.useEffect(
    function handleEmailChange() {
      (async email => {
        if (!authStore.isAuthorized && !errors.email) {
          const { emailTaken } = await authStore.checkEmail({ email });
          setIsEmailTaken(emailTaken);
          if (emailTaken) {
            setShowSignInModal(true);
          }
        }
      })(values.email);
    },
    [authStore, userStore, values.email, errors.email]
  );

  const handleValidate = async () => {
    if (isEmailTaken) {
      await validateFormikForm({ validateForm, setErrors, setTouched });
      setStatus({ errors: ['email_taken'] });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      if (status) {
        setStatus();
      }
      submitForm();
    }
  };

  return (
    <div>
      {showSignInModal && (
        <SignInModal initialValues={{ email: values.email }} onClose={() => setShowSignInModal(false)}>
          {{
            title: (
              <Title fontSize={16} lineHeight="normal">
                This email address is already used. It means you already applied through the Sainoo recruiting system.
                Please login or{' '}
                <Link to={ROUTES.forgotPassword} variant="auth" display="inline">
                  {' '}
                  recover your password{' '}
                </Link>
                .
              </Title>
            )
          }}
        </SignInModal>
      )}
      {initialValues.first_name && (
        <>
          <Title>Thanks {initialValues.first_name}! Can you check we have the right contact information?</Title>
          <Delimiter my={30} />
        </>
      )}
      <div>
        <Flex mb={20} justifyContent="space-between">
          <FormikInput
            name="first_name"
            label="First name"
            placeholder="First name"
            size="md"
            flex="0 1 calc(50% - 15px)"
          />
          <FormikInput
            name="last_name"
            label="Last name"
            placeholder="Last name"
            size="md"
            flex="0 1 calc(50% - 15px)"
          />
        </Flex>
        <Flex justifyContent="space-between" flexWrap="wrap">
          <FormikInput name="email" label="Email" placeholder="Email" mb={15} flex="0 1 calc(50% - 15px)" />
          {job.application_form_settings?.phone && (
            <FormikInput name="phone" label="Phone number" placeholder="Phone" mb={15} flex="0 1 calc(50% - 15px)" />
          )}
          {job.application_form_settings?.country && (
            <FormikCombobox
              minWidth={isIE11 && 400}
              name="country"
              label="Country"
              placeholder="Country"
              items={resourcesStore.countries}
              mb={15}
              flex="0 1 calc(50% - 15px)"
            />
          )}
        </Flex>
      </div>
      <Flex justifyContent="flex-end">
        <Tooltip disabled={!job.is_applied} title={job.is_applied && 'You already applied to this position'}>
          <FormikSubmitButton
            type="button"
            onClick={handleValidate}
            disabled={job.is_applied}
            variant="primary"
            size="md"
          >
            {job.skipExternalUrl
              ? isValidating
                ? 'Validating...'
                : 'Validate'
              : isValidating
              ? 'Applying...'
              : 'Apply'}
          </FormikSubmitButton>
        </Tooltip>
      </Flex>
    </div>
  );
};

export default GeneralForm;
